/**
 * Which rows of this panel the super admin left visible for this brand.
 *
 * The rail used to be a fixed list gated only by the signed-in user's own role,
 * so a white-label owner saw every page the build shipped. The super admin now
 * sets a per-brand map (Owner Management → Owner's panel), which `GET
 * /brand/admin-menus` answers for whoever is signed in.
 *
 * The ids MUST stay in step with the backend's `brand-admin-menus.ts` and the
 * `menu` keys on `sideBarContent` in `EnhancedSidebar.tsx` — one vocabulary
 * living in three repos, exactly like the player site's Menu Visibility.
 *
 * A row is hidden only when the map explicitly says `false`. An id the brand
 * never stored, a failed request and a user with no brand all mean "show it",
 * so nobody is locked out of their own panel by a lookup that did not answer.
 */
export type AdminMenus = Record<string, boolean>;

/** Non-reactive read for code outside the React tree (route helpers). */
let snapshot: AdminMenus = {};
const subs = new Set<() => void>();

export function setAdminMenus(raw: unknown): void {
  snapshot = raw && typeof raw === 'object' ? (raw as AdminMenus) : {};
  subs.forEach((f) => f());
}

export function subscribeAdminMenus(cb: () => void): () => void {
  subs.add(cb);
  return () => { subs.delete(cb); };
}

export function getAdminMenus(): AdminMenus {
  return snapshot;
}

/** True unless the brand explicitly switched this row off. */
export function isAdminMenuOn(id: string | undefined, menus: AdminMenus): boolean {
  return id === undefined || menus[id] !== false;
}
